import {ref, watch, type Ref} from 'vue';
import {useApi} from '@/ts/composables/stateful/use-api';
import {useLogger} from '@/ts/composables/pure/use-logger';
import {
  selectExploreArrivingCover,
  selectExploreRingCovers,
  type ExploreRingCovers,
} from '@/ts/composables/pure/use-explore-ring-covers';
import type {ComponentContent} from '@/ts/types/dto/interventions-dto';

/**
 * All the reader waits, for the band and its pictures together — the page holds its own loading
 * state open for this, so what is bounded has to be the whole of it and not each stage. Long
 * enough for a band and ten covers off an ordinary connection, and short enough that a dead one
 * costs a wait rather than a page.
 *
 * It has to be bounded at all because `fetch` is called without a signal anywhere in this app: a
 * request that never settles never rejects either.
 */
const CEILING_MS = 4000;

/** Whatever the work answers, or the fallback once the ceiling is up. */
async function withCeiling<T>(work: Promise<T>, fallback: T): Promise<T> {
  let timer: ReturnType<typeof setTimeout>;

  return Promise.race([
    work,
    new Promise<T>((resolve) => {
      timer = setTimeout(() => {
        resolve(fallback);
      }, CEILING_MS);
    }),
  ]).finally(() => {
    // The loser of the race is nobody's business, and a timer left running is one a test with a
    // fake clock still has to wait out.
    clearTimeout(timer);
  });
}

/** Resolves when the browser has the picture, and when it gives up on it. */
async function preload(url: string): Promise<void> {
  await new Promise<void>((resolve) => {
    const done = (): void => {
      resolve();
    };
    const image = new Image();
    image.addEventListener('load', done);
    image.addEventListener('error', done);
    image.src = url;
  });
}

interface ExploreRingCoversState {
  /** What the ring is drawn with. Empty while the band is on its way, and if it never arrives. */
  covers: Ref<ExploreRingCovers>;
  /**
   * Nothing is on its way: every picture the ring needs is in the browser, or there is none to
   * have. Nothing about the ring changes after this, which is what lets the card wait for it.
   */
  settled: Ref<boolean>;
  /**
   * The first read is over, whatever it came back with — a band, or nothing at all because it
   * never answered. It never goes back to false, which is the difference from `settled`: the band
   * is read again when the reader answers, and a page that took that for a first read would blank
   * the bento under them. Ask this one when the question is "has the ring been drawn".
   */
  drawn: Ref<boolean>;
  /**
   * Start now, before the payload has asked for a ring. For a caller that can see one coming — a
   * reader in a questionnaire is about to be told their content has changed — and would otherwise
   * spend the answer, the refresh and ten downloads one after the other.
   */
  prepare(): void;
  /**
   * The reader has answered, so the platform has chosen for them since the ring was read. The band
   * is read again for the piece that drops in, and only for that: one picture to download rather
   * than ten, which is the difference between the card changing as they watch and seconds later.
   */
  refreshArrival(): void;
}

/**
 * The reader's own recommendation covers for the Explore tile's ring (IPD-3256).
 *
 * The band is asked for beside the payload that decides whether it is needed rather than after it,
 * so it is in hand by the time the answer is. The pictures are the expensive half and are only
 * downloaded for a tile that is going to draw them.
 */
export function useExploreRingCovers(
  wantsRing: () => boolean,
  canAccessExplore: () => boolean,
): ExploreRingCoversState {
  const api = useApi();
  const logger = useLogger();
  const covers = ref<ExploreRingCovers>({});
  const settled = ref(false);
  const drawn = ref(false);

  // Read once and held: its failure is absorbed here, since nothing awaits it but the work below
  // and a decorative band must not surface as an unhandled rejection.
  //
  // Not asked for at all by a reader whose licence blacklists Explore: the page drops all three of
  // its tiles, so there is no ring for this to fill. The flag only reads false for a profile that
  // has loaded and says so, which is why asking it this early is safe.
  const band: Promise<ComponentContent[]> = canAccessExplore()
    ? api.content.getRecommendations()
      .then((items) => items ?? [])
      .catch((err: unknown) => {
        logger.error('Failed to fetch the Explore tile ring covers', {err})();

        return [];
      })
    : Promise.resolve([]);

  // One at a time and in the order they were asked for: the arrival is chosen against the ring, so
  // it cannot be read before the ring is. `settled` says the queue is empty rather than that the
  // last piece of work finished, or a card would change on its way through it.
  let pending = 0;
  let queue: Promise<void> = Promise.resolve();
  function enqueue(work: () => Promise<void>): void {
    pending += 1;
    settled.value = false;
    queue = queue
      .then(work)
      .catch((err: unknown) => {
        // Decorative, so it stays out of the page's error state: the stock ring is a complete card.
        logger.error('Failed to prepare the Explore tile ring covers', {err})();
      })
      .finally(() => {
        pending -= 1;
        if (pending === 0) {
          settled.value = true;
        }
      });
  }

  /** The band and every picture on it, which is what the ring is drawn from and nothing less. */
  async function readBand(): Promise<ExploreRingCovers> {
    const selected = selectExploreRingCovers(await band);
    // The pictures, not just their addresses: the ring is drawn once they are in the browser, so
    // the covers never change after the reader has seen them.
    await Promise.all([...selected.ringTiles ?? [], ...selected.arrivingTile ? [selected.arrivingTile] : []]
      .map(async(url) => preload(url)));

    return selected;
  }

  let asked = false;
  function prepare(): void {
    if (asked) {
      return;
    }
    asked = true;
    // One ceiling over the band and its pictures together, rather than one each: the page waits on
    // this, and two in a row is twice the home page spent on a ring nobody asked for.
    enqueue(async() => {
      try {
        covers.value = await withCeiling(readBand(), {});
      } finally {
        // Whatever came of it, the waiting is over: a read that answered with nothing still leaves
        // the tile a ring to draw, its own.
        drawn.value = true;
      }
    });
  }

  function refreshArrival(): void {
    enqueue(async() => {
      const fresh = await withCeiling(api.content.getRecommendations().then((items) => items ?? []), []);
      const arriving = selectExploreArrivingCover(fresh, covers.value.ringTiles);
      if (!arriving) {
        return;
      }
      await preload(arriving);
      covers.value = {...covers.value, arrivingTile: arriving};
    });
  }

  watch(wantsRing, (wants) => {
    if (wants) {
      prepare();
    }
  }, {immediate: true});

  return {covers, settled, drawn, prepare, refreshArrival};
}
